import React, { useEffect, useContext, useState, useRef } from 'react';
import FadeIn from 'react-fade-in';
import { GlobalHotKeys } from 'react-hotkeys';
import { useObserver } from 'mobx-react-lite';

import QuickDrawerHeader from '../../../../_shared/QuickDrawerHeader';
import { quickDrawerFocus } from '../../../../_shared/QuickDrawer';
import RichTextEditor from '../../../../_shared/RichTextEditor';

import PretestInputUpdateStore from '../../../../../../stores/PretestInputUpdateStore';
import QuickDrawerStore from '../../../../../../stores/QuickDrawerStore';
import CacheStore from '../../../../../../stores/CacheStore';

import * as ErrorMessages from '../../../../../../constants/errorMessages';
import * as fn from '../../../../../../utilities/_functions';
import * as oh from '../../../../../../utilities/operationHelper';
import * as tih from '../../../../../../utilities/templateInputHelper';

import './UpdatePretestInput.scss';

function UpdatePretestInput(props) {
    const isMounted = useRef(true);
    const focusTimer = useRef(null);
    const pretestInput = useContext(PretestInputUpdateStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const cache = useContext(CacheStore);
    const [size, setSize] = useState('');
    const [align, setAlign] = useState('');
    const [valign, setValign] = useState('');
    const [options, setOptions] = useState([]);
    const [inline, setInline] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [imageWidth, setImageWidth] = useState(null);
    const [imageHeight, setImageHeight] = useState(null);
    const [defaultValue, setDefaultValue] = useState(null);
    const [deviceMapping, setDeviceMapping] = useState(null);
    const [dataType, setDataType] = useState(null);
    const [paddingType, setPaddingType] = useState(null);
    const [paddingCharacter, setPaddingCharacter] = useState(null);
    const [paddingLength, setPaddingLength] = useState(null);
    const [decimalPlaces, setDecimalPlaces] = useState(null);
    const [isDefaultNegative, setIsDefaultNegative] = useState(null);
    const [format, setFormat] = useState(null);
    const [formatSearch, setFormatSearch] = useState(null);
    const [formatReplace, setFormatReplace] = useState(null);
    const [validation, setValidation] = useState(null);
    const [step, setStep] = useState(null);

    useEffect(() => {
        focusTimer.current = setTimeout(() => {
            setSize(pretestInput.getMetadata('size'));
            setAlign(pretestInput.getMetadata('align'));
            setValign(pretestInput.getMetadata('valign'));
            setOptions(pretestInput.getMetadata('options'));
            setInline(pretestInput.getMetadata('inline'));
            setImageUrl(pretestInput.getMetadata('imageUrl'));
            setImageWidth(pretestInput.getMetadata('imageWidth'));
            setImageHeight(pretestInput.getMetadata('imageHeight'));
            setDefaultValue(pretestInput.getMetadata('defaultValue'));
            setDeviceMapping(pretestInput.getMetadata('deviceMapping'));
            setDataType(pretestInput.getMetadata('dataType'));
            setPaddingType(pretestInput.getMetadata('paddingType'));
            setPaddingCharacter(pretestInput.getMetadata('paddingCharacter'));
            setPaddingLength(pretestInput.getMetadata('paddingLength'));
            setIsDefaultNegative(pretestInput.getMetadata('isDefaultNegative'));
            setDecimalPlaces(pretestInput.getMetadata('decimalPlaces'));
            setFormat(pretestInput.getMetadata('format'));
            setFormatSearch(pretestInput.getMetadata('formatSearch'));
            setFormatReplace(pretestInput.getMetadata('formatReplace'));
            setValidation(pretestInput.getMetadata('validation'));
            setStep(pretestInput.getMetadata('step'));
            quickDrawerFocus(props.drawer);
        }, 100)

        return (() => {
            isMounted.current = false;
            pretestInput.clear();
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        })
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (pretestInput.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = event => {
        const updated = pretestInput.hasUnsavedChanges;

        event.preventDefault();
        handlePopulateMetadata();

        pretestInput.save()
            .then(data => {
                if (isMounted.current) {
                    if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                        props.onSuccess(event, { updated: updated, data: { pretestInput: data } });
                    }
                }
            })
    }

    const handleLabelChange = event => {
        pretestInput.data.label = event.target.value;
        pretestInput.hasUnsavedChanges = true;
    }

    const handleDescriptionChange = content => {
        const html = content;
        pretestInput.data.descriptionHtml = ((html === '<p><br></p>') ? null : html);
        pretestInput.hasUnsavedChanges = true;
    }

    const handleSizeChange = event => {
        setSize(event.target.value);
        pretestInput.hasUnsavedChanges = true;
    }

    const handleAlignChange = value => {
        setAlign((value ? value : 'left'));
        pretestInput.hasUnsavedChanges = true;
    }

    const handleValignChange = value => {
        setValign((value ? value : 'top'));
        pretestInput.hasUnsavedChanges = true;
    }

    const handleInlineChange = event => {
        setInline((event.target.value === 'true'));
        pretestInput.hasUnsavedChanges = true;
    }

    const handlePopulateMetadata = () => {
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_SIZE, 'size', size);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_ALIGN, 'align', align);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_VALIGN, 'valign', valign);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_OPTIONS, 'options', options);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_INLINE, 'inline', inline);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_IMAGE_URL, 'imageUrl', imageUrl);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_IMAGE_URL, 'imageWidth', imageWidth);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_IMAGE_URL, 'imageHeight', imageHeight);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_MAPPING, 'deviceMapping', deviceMapping);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_TYPE, 'dataType', dataType);
        if (!!paddingType && !!paddingCharacter && !!paddingLength) {
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_TYPE, 'paddingType', paddingType);
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_CHARACTER, 'paddingCharacter', paddingCharacter);
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_LENGTH, 'paddingLength', paddingLength);
        }
        else {
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_TYPE, 'paddingType', null);
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_CHARACTER, 'paddingCharacter', null);
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_LENGTH, 'paddingLength', null);
        }
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_DEFAULT_NEGATIVE, 'isDefaultNegative', isDefaultNegative);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_DECIMAL_PLACES, 'decimalPlaces', decimalPlaces);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT, 'format', format);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT_SEARCH, 'formatSearch', formatSearch);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT_REPLACE, 'formatReplace', formatReplace);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_VALIDATION, 'validation', validation);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_STEP, 'step', step);
    }

    const handleImageAnnotationChange = event => {
        const file = event.target.files[0];
        const reader = new FileReader();

        reader.onload = handleFileReaderLoad;
        reader.readAsDataURL(file);
        pretestInput.hasUnsavedChanges = true;
    }

    const handleFileReaderLoad = (event) => {
        const reader = event.target;
        const base64 = reader.result;
        const tempImage = new Image();

        tempImage.src = event.target.result;
        tempImage.onload = () => {
            setImageWidth(tempImage.width);
            setImageHeight(tempImage.height);
        }
        setImageUrl(base64);
    }

    const handleImageAnnotationChangeClick = event => {
        setImageUrl(null);
        setImageWidth(null);
        setImageHeight(null);
        pretestInput.hasUnsavedChanges = true;
    }

    const handleDeviceMapping = event => {
        const value = event.target.value;
        setDeviceMapping(value);
    }

    const handleDataTypeChange = event => {
        const value = event.target.value;

        switch (value) {
            case 'String':
                setIsDefaultNegative(null);
                setDecimalPlaces(null);
                setFormat(null);
                setValidation(null);
                break;

            case 'Integer':
                setIsDefaultNegative(false);
                setDecimalPlaces(null);
                setFormat(null);
                setValidation(null);
                clearPadding();
                break;

            case 'Decimal':
                setIsDefaultNegative(false);
                setDecimalPlaces(2);
                setFormat(null);
                setValidation(null);
                clearPadding();
                break;

            default:
                setIsDefaultNegative(null);
                setDecimalPlaces(null);
                setFormat(null);
                setValidation(null);
                clearPadding();
                break;
        }

        setDataType(value);
        pretestInput.hasUnsavedChanges = true;
    }

    const handlePaddingTypeChange = event => {
        const value = event.target.value;
        setPaddingType(value);
        pretestInput.hasUnsavedChanges = true;

        if (!value) {
            setPaddingCharacter(null);
            setPaddingLength(null);
        }
    }

    const handlePaddingCharacterChange = event => {
        const value = event.target.value;
        setPaddingCharacter(value);
        pretestInput.hasUnsavedChanges = true;

        if (!paddingLength) {
            if (value && value.length > 0) {
                setPaddingLength(2)
            }
        }
    }

    const handlePaddingBlur = event => {
        if (paddingCharacter && !paddingLength) {
            setPaddingLength(2)
        }
        if (!paddingCharacter && paddingLength) {
            setPaddingLength(null)
        }
    }

    const handlePaddingLengthChange = event => {
        const value = parseInt(event.target.value, 10);
        setPaddingLength(value);
        pretestInput.hasUnsavedChanges = true;
    }

    const handleDefaultNegativeChange = event => {
        const value = !isDefaultNegative;
        setIsDefaultNegative(value);
        pretestInput.hasUnsavedChanges = true;
    }

    const handleDecimalPlacesChange = event => {
        const value = parseInt(event.target.value, 10);
        setDecimalPlaces(value);
        pretestInput.hasUnsavedChanges = true;
    }

    const handleFormatClick = event => {
        const value = event.target.value;

        if (value === format) {
            setFormat(null);
            pretestInput.hasUnsavedChanges = true;
        }
    }

    const handleFormatChange = event => {
        const value = event.target.value;
        setFormat(value);
        pretestInput.hasUnsavedChanges = true;
    }

    const handleFormatSearchChange = event => {
        const value = event.target.value;
        setFormatSearch(value);
        pretestInput.hasUnsavedChanges = true;
    }

    const handleFormatReplaceChange = event => {
        const value = event.target.value;
        setFormatReplace(value);
        pretestInput.hasUnsavedChanges = true;
    }

    const handleValidationChange = event => {
        const value = event.target.value;
        setValidation(value);
        pretestInput.hasUnsavedChanges = true;
    }

    const handleStepChange = event => {
        const value = event.target.value;
        const number = !fn.isNullOrUndefined(value) ? Number(value) : null;
        const isDecimal = !isNaN(number) && !Number.isInteger(number);
        const isInt = !isNaN(number) && Number.isInteger(number);

        if (isDecimal || isInt) {
            setStep(value);
        }
        else {
            setStep(null);
        }

        pretestInput.hasUnsavedChanges = true;
    }

    const handleDefaultValueTextChange = event => {
        setDefaultValue(event.target.value);
        pretestInput.hasUnsavedChanges = true;
    }

    const handleDefaultValueRichTextChange = content => {
        const html = content;
        setDefaultValue(((html === '<p><br></p>') ? null : html));
        pretestInput.hasUnsavedChanges = true;
    }

    const handleDefaultValueCheckboxChange = event => {
        if (!defaultValue) {
            setDefaultValue([])
        }

        if (event.target.checked) {
            defaultValue.push(event.target.value);
        } else {
            const index = defaultValue.findIndex(v => v === event.target.value);
            if (index >= 0) {
                defaultValue.splice(index, 1);
            }
        }

        if (defaultValue.length === 0) {
            setDefaultValue(null);
        }

        setDefaultValue(defaultValue);
        pretestInput.hasUnsavedChanges = true;
    }

    const handleOptionChange = (event, index) => {
        const tempOptions = [...options];
        tempOptions[index] = event.target.value;
        setOptions(tempOptions);
        pretestInput.hasUnsavedChanges = true;
    }

    const handleAddOption = event => {
        event.preventDefault();
        setOptions(o => [...options, '']);
        focusTimer.current = setTimeout(() => {
            const element = document.querySelector(`#update-pretest-input-${pretestInput.data.key}-${(options.length)}`)
            if (element) { element.focus(); }
        }, 50)
        pretestInput.hasUnsavedChanges = true;
    }

    const handleRemoveOption = (option, index) => {
        setOptions(options.filter((o, oi) => oi !== index));
        pretestInput.hasUnsavedChanges = true;
    }

    const handleMoveOption = (from, to) => {
        const tempOptions = [...options];
        const element = tempOptions[from];

        tempOptions.splice(from, 1);
        tempOptions.splice(to, 0, element);

        setOptions(tempOptions);
        pretestInput.hasUnsavedChanges = true;
    }

    const clearPadding = () => {
        setPaddingType(null);
        setPaddingCharacter(null);
        setPaddingLength(null);
    }

    const renderOptions = () => {
        return <>
            {
                options && options.length > 0 ?
                    options.map((o, oi) => {
                        return <ul
                            key={`pretest_input_options_${oi}`}
                            className='list-inline no-style m-0 mb-2'
                        >
                            <li className='list-inline-item m-0 mr-1'>{oi + 1}.&nbsp;</li>
                            <li className='list-inline-item m-0 option'>
                                <input
                                    id={`update-pretest-input-${pretestInput.data.key}-${oi}`}
                                    type='text'
                                    className='form-control form-control-sm'
                                    autoComplete='off'
                                    value={o}
                                    onChange={(e) => handleOptionChange(e, oi)}
                                />
                            </li>
                            <li className='list-inline-item m-0 ml-2'>
                                <button
                                    type='button'
                                    className='btn btn-link btn-icon btn-sm'
                                    autoComplete='off'
                                    onClick={e => { handleRemoveOption(o, oi) }}
                                >
                                    <i className={'fal fa-minus-circle text-danger'}></i>
                                </button>
                            </li>
                            <li className='list-inline-item m-0'>
                                <button
                                    type='button'
                                    className='btn btn-link btn-icon btn-sm'
                                    autoComplete='off'
                                    disabled={options.length === 1 || oi === 0}
                                    onClick={e => { handleMoveOption(oi, (oi - 1)) }}
                                >
                                    <i className={'fal fa-arrow-up' + (options.length === 1 || oi === 0 ? ' text-gray-500' : ' text-primary')}></i>
                                </button>
                            </li>
                            <li className='list-inline-item m-0'>
                                <button
                                    type='button'
                                    className='btn btn-link btn-icon btn-sm'
                                    disabled={options.length === 1 || oi === (options.length - 1)}
                                    onClick={e => { handleMoveOption(oi, (oi + 1)) }}
                                >
                                    <i className={'fal fa-arrow-down' + (options.length === 1 || oi === (options.length - 1) ? ' text-gray-500' : ' text-primary')}></i>
                                </button>
                            </li>
                        </ul>
                    }) : <div></div>
            }
            <button
                type='button'
                className='btn btn-link btn-icon-left btn-sm'
                onClick={handleAddOption}
            >
                <i className='fal fa-plus-circle'></i> Add option
            </button>
        </>
    }

    const renderDefaultValueEditor = () => {
        switch (pretestInput.data.type) {
            case 'SingleLineText':
                return <input
                    id='update-pretest-input-default'
                    type='text'
                    maxLength={100}
                    className='form-control'
                    value={defaultValue ? defaultValue : ''}
                    autoComplete={'off'}
                    onChange={handleDefaultValueTextChange}
                />;

            case 'MultiLineText':
                return <textarea
                    id='update-pretest-input-default'
                    className='form-control'
                    value={defaultValue ? defaultValue : ''}
                    autoComplete={'off'}
                    onChange={handleDefaultValueTextChange}
                />;
            // return <RichTextEditor
            //     id='update-pretest-input-default'
            //     mode='none'
            //     disableTab={true}
            //     value={defaultValue ? defaultValue : ''}
            //     onChange={handleDefaultValueRichTextChange}
            // />;

            case 'RichText':
                return <RichTextEditor
                    id='update-pretest-input-default'
                    mode='full'
                    disableTab={true}
                    value={defaultValue ? defaultValue : ''}
                    onChange={handleDefaultValueRichTextChange}
                />;

            case 'Dropdown':
                return <select
                    id='update-pretest-input-default'
                    className='custom-select form-control'
                    value={defaultValue}
                    onChange={handleDefaultValueTextChange}
                >
                    <option value=''></option>
                    {
                        options && options.length > 0 ?
                            options.map((o, oi) => {
                                return <option
                                    key={`${pretestInput.key}_metadata_${oi}`}
                                    value={o}>{o}</option>
                            }) : null
                    }
                </select>;

            case 'Radio':
                return <div className='radio-wrapper'>
                    {
                        options && options.length > 0 ?
                            options.map((o, oi) => {
                                return <div
                                    key={`${pretestInput.key}_metadata_${oi}`}
                                    className={'custom-control custom-radio mb-2'}
                                >
                                    <input
                                        id={`update-pretest-input-default-${pretestInput.key}-${oi}`}
                                        type='radio'
                                        name='pretest-input-default'
                                        className='custom-control-input'
                                        value={o}
                                        checked={defaultValue === o}
                                        onChange={handleDefaultValueTextChange}
                                    />
                                    <label
                                        htmlFor={`update-pretest-input-inline-${pretestInput.key}-${oi}`}
                                        className='custom-control-label'
                                    >
                                        {o}
                                    </label>
                                </div>
                            }) : null
                    }
                </div>;

            case 'Checkbox':
                return <div className='checkbox-wrapper'>
                    {
                        options && options.length > 0 ?
                            options.map((o, oi) => {
                                return <div
                                    key={`${pretestInput.key}_metadata_${oi}`}
                                    className={'custom-control custom-checkbox mb-2'}
                                >
                                    <input
                                        id={`update-pretest-input-default-${pretestInput.key}-${oi}`}
                                        type='checkbox'
                                        name='pretest-input-default'
                                        className='custom-control-input'
                                        value={o}
                                        checked={defaultValue && defaultValue.split('|').some(v => v === o)}
                                        onChange={handleDefaultValueCheckboxChange}
                                    />
                                    <label
                                        htmlFor={`update-pretest-input-inline-${pretestInput.key}-${oi}`}
                                        className='custom-control-label'
                                    >
                                        {o}
                                    </label>
                                </div>
                            }) : null
                    }
                </div>;

            case 'Date':
            case 'Time':
            default:
                return null
        }
    }

    const populateMetadata = (field, key, value) => {
        if (tih.hasField(pretestInput.data.type, field)) {
            pretestInput.setMetadata(key, value);
        } else {
            pretestInput.removeMetadata(key);
        }
    }

    return useObserver(() => <>
        <>
            {
                (props.drawer === quickDrawer.drawerOpened) ?
                    <GlobalHotKeys
                        keyMap={{
                            close: ['esc'],
                        }}
                        handlers={{
                            close: event => {
                                handleCancel(event)
                            },
                        }}
                        allowChanges={true}
                    /> : null
            }
        </>
        <form onSubmit={handleSubmit}>
            <fieldset disabled={pretestInput.isSaving}>
                <div className='quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('pretest-input', 'default')}
                        action='Update'
                        category='Pretest Input'
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        <FadeIn>
                            <div className='update-pretest-input body-content'>
                                <section>
                                    <div className='row'>
                                        <div className='col-12'>
                                            <div className='form-group mb-4'>
                                                <label className='required' htmlFor='update-pretest-input-type'><small>Type</small></label>
                                                <select
                                                    id='update-pretest-input-type'
                                                    className='custom-select form-control'
                                                    value={pretestInput.data.type ? pretestInput.data.type : ''}
                                                    disabled={true}
                                                >
                                                    <option value=''></option>
                                                    {
                                                        cache.getReferenceDataOptions('TemplateInputType').map((option, di) => {
                                                            return <option key={`pretest_input_type_${di}`} value={option.key}>{option.value}</option>
                                                        })
                                                    }
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='row'>
                                        <div className='col-12'>
                                            <div className='form-group mb-4'>
                                                <label className='required' htmlFor='update-pretest-input-name'><small>Name</small></label>
                                                <input
                                                    id='update-pretest-input-name'
                                                    type='text'
                                                    maxLength={100}
                                                    className='form-control'
                                                    autoComplete={'off'}
                                                    disabled={true}
                                                    value={pretestInput.data.name ? pretestInput.data.name : ''}
                                                />
                                                <small className='text-gray-700'><strong>Important:</strong> Name must be unique across all inputs.</small>
                                            </div>
                                        </div>
                                    </div>
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_LABEL) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-pretest-input-label'><small>Label</small></label>
                                                        <input
                                                            id='update-pretest-input-label'
                                                            type='text'
                                                            maxLength={100}
                                                            className='form-control'
                                                            value={pretestInput.data.label ? pretestInput.data.label : ''}
                                                            autoComplete={'off'}
                                                            onChange={handleLabelChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_SIZE) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label className='required' htmlFor='update-pretest-input-size'><small>Size</small></label>
                                                        <select
                                                            id='update-pretest-input-size'
                                                            className='custom-select form-control'
                                                            value={size ? size : ''}
                                                            onChange={handleSizeChange}
                                                        >
                                                            <option value='sm'>Small</option>
                                                            <option value=''>Standard</option>
                                                            <option value='lg'>Large</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_ALIGN) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label className='required' htmlFor='update-pretest-input-align'><small>Align</small></label>
                                                        <div>
                                                            <div className='btn-group'>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-primary' + (align === 'left' ? ' active' : '')}
                                                                    value={(align ? align : '')}
                                                                    onClick={() => { handleAlignChange('left') }}
                                                                >
                                                                    <i className='fal fa-align-left'></i>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-primary' + (align === 'center' ? ' active' : '')}
                                                                    value={(align ? align : '')}
                                                                    onClick={() => { handleAlignChange('center') }}
                                                                >
                                                                    <i className='fal fa-align-center'></i>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-primary' + (align === 'right' ? ' active' : '')}
                                                                    value={(align ? align : '')}
                                                                    onClick={() => { handleAlignChange('right') }}
                                                                >
                                                                    <i className='fal fa-align-right'></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_VALIGN) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label className='required' htmlFor='update-pretest-input-valign'><small>Vertical Align</small></label>
                                                        <div>
                                                            <div className='btn-group'>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-primary' + (valign === 'top' ? ' active' : '')}
                                                                    value={(valign ? valign : '')}
                                                                    onClick={() => { handleValignChange('top') }}
                                                                >
                                                                    <i className='fal fa-arrow-to-top'></i>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-primary' + (valign === 'middle' ? ' active' : '')}
                                                                    value={(valign ? valign : '')}
                                                                    onClick={() => { handleValignChange('middle') }}
                                                                >
                                                                    <i className='fal fa-horizontal-rule'></i>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-primary' + (valign === 'bottom' ? ' active' : '')}
                                                                    value={(valign ? valign : '')}
                                                                    onClick={() => { handleValignChange('bottom') }}
                                                                >
                                                                    <i className='fal fa-arrow-to-bottom'></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_INLINE) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label><small>Inline</small></label>
                                                        <div>
                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                <input
                                                                    id={`update-pretest-input-inline-true`}
                                                                    type='radio'
                                                                    name='pretest-input-inline'
                                                                    className='custom-control-input'
                                                                    value={true}
                                                                    checked={inline}
                                                                    onChange={handleInlineChange}
                                                                />
                                                                <label
                                                                    htmlFor={`update-pretest-input-inline-true`}
                                                                    className='custom-control-label'
                                                                >
                                                                    Yes
                                                                </label>
                                                            </div>
                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                <input
                                                                    id={`update-pretest-input-inline-false`}
                                                                    type='radio'
                                                                    name='pretest-input-inline'
                                                                    className='custom-control-input'
                                                                    value={false}
                                                                    checked={!inline}
                                                                    onChange={handleInlineChange}
                                                                />
                                                                <label
                                                                    htmlFor={`update-pretest-input-inline-false`}
                                                                    className='custom-control-label'
                                                                >
                                                                    No
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_IMAGE_URL) ?
                                            <div className='row options'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label className='required' htmlFor='update-pretest-input-options'><small>Image</small></label>
                                                        {
                                                            imageUrl ?
                                                                <div>
                                                                    <img src={imageUrl} width={imageWidth} height={imageHeight} alt='Annotate' />
                                                                    <button
                                                                        type='button'
                                                                        className='btn btn-link px-0 btn-sm d-block'
                                                                        onClick={handleImageAnnotationChangeClick}
                                                                    >Change image</button>
                                                                </div> :
                                                                <div className='custom-file'>
                                                                    <input
                                                                        id='update-pretest-input-image-url'
                                                                        type='file'
                                                                        className='custom-file-input'
                                                                        accept='image/*'
                                                                        onChange={handleImageAnnotationChange} />
                                                                    <label
                                                                        htmlFor='update-pretest-input-image-url'
                                                                        className='custom-file-label'>
                                                                    </label>
                                                                </div>
                                                        }
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_OPTIONS) ?
                                            <div className='row options'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label className='required' htmlFor='update-pretest-input-options'><small>Options</small></label>
                                                        {renderOptions()}
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        props.extraProps && props.extraProps && props.extraProps.industryDevice && tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_MAPPING) ?
                                            <>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-4'>
                                                            <label htmlFor='update-pretest-input-device'><small>Logical Device (Grouping)</small></label>
                                                            <input
                                                                type='text'
                                                                className='form-control'
                                                                value={props.extraProps.industryDevice.name}
                                                                title={props.extraProps.industryDevice.name}
                                                                disabled={true}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-4'>
                                                            <label htmlFor='update-pretest-input-device-mapping'><small>Data mapping</small></label>
                                                            <select
                                                                id='update-pretest-input-device-mapping'
                                                                className='custom-select form-control'
                                                                value={deviceMapping ? deviceMapping : ''}
                                                                onChange={handleDeviceMapping}
                                                            >
                                                                <option value=''></option>
                                                                {
                                                                    props.extraProps.industryDevice && props.extraProps.industryDevice.deviceToPcFixedWidthMappings && props.extraProps.industryDevice.deviceToPcFixedWidthMappings.length > 0 ?
                                                                        props.extraProps.industryDevice.deviceToPcFixedWidthMappings.map((m, mi) => {
                                                                            return <option
                                                                                key={`pretest_input_mapping_${mi}`}
                                                                                value={m.id}
                                                                            >{`${m.name} (${m.id})`}</option>
                                                                        }) : null
                                                                }
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            </> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_TYPE) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-pretest-input-data-type'><small>Data Type</small></label>
                                                        <select
                                                            id='update-pretest-input-data-type'
                                                            className='custom-select form-control'
                                                            value={dataType ? dataType : ''}
                                                            onChange={handleDataTypeChange}
                                                        >
                                                            <option value=''>(Not set)</option>
                                                            {
                                                                tih.getDataTypes().map((dt, dti) => {
                                                                    return <option
                                                                        key={`update-pretest-input-data-type_${dti}`}
                                                                        value={dt}
                                                                    >{dt}</option>
                                                                })
                                                            }
                                                        </select>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_TYPE) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_TYPE) ?
                                            <div className='row'>
                                                <div className='col-4'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-pretest-input-padding-type'><small>Data Padding</small></label>
                                                        <select
                                                            id='update-pretest-input-padding-type'
                                                            className='custom-select form-control'
                                                            value={paddingType ? paddingType : ''}
                                                            onChange={handlePaddingTypeChange}
                                                        >
                                                            <option value=''>(None)</option>
                                                            <option value='left'>Left</option>
                                                            <option value='right'>Right</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className='col-4'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-pretest-input-padding-char'><small>Character</small></label>
                                                        <input
                                                            id='update-pretest-input-padding-char'
                                                            type='text'
                                                            maxLength={1}
                                                            className='form-control'
                                                            autoComplete='off'
                                                            value={paddingCharacter ? paddingCharacter : ''}
                                                            disabled={!paddingType}
                                                            onChange={handlePaddingCharacterChange}
                                                            onBlur={handlePaddingBlur}
                                                        />
                                                    </div>
                                                </div>
                                                <div className='col-4'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-pretest-input-padding-length'><small>Length</small></label>
                                                        <input
                                                            id='update-pretest-input-padding-length'
                                                            type='number'
                                                            min={2}
                                                            max={10}
                                                            step={1}
                                                            className='form-control'
                                                            autoComplete='off'
                                                            disabled={!paddingCharacter || paddingCharacter.length === 0}
                                                            value={paddingLength ? paddingLength : ''}
                                                            onChange={handlePaddingLengthChange}
                                                            onBlur={handlePaddingBlur}
                                                        />
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_DEFAULT_NEGATIVE) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_DEFAULT_NEGATIVE) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <div className='custom-control custom-switch'>
                                                            <input
                                                                id='update-pretest-input-default-negative'
                                                                type='checkbox'
                                                                name='update-pretest-input-default-negative'
                                                                className='custom-control-input'
                                                                checked={isDefaultNegative}
                                                                onChange={handleDefaultNegativeChange}
                                                            />
                                                            <label
                                                                htmlFor='update-pretest-input-default-negative'
                                                                className='custom-control-label'
                                                            >
                                                                Default as negative number
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_DECIMAL_PLACES) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_DECIMAL_PLACES) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-pretest-input-decimal-places'><small>Decimal Places</small></label>
                                                        <input
                                                            id='update-pretest-input-decimal-places'
                                                            type='number'
                                                            min={1}
                                                            max={5}
                                                            step={1}
                                                            className='form-control'
                                                            autoComplete='off'
                                                            value={decimalPlaces ? decimalPlaces : ''}
                                                            onChange={handleDecimalPlacesChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-pretest-input-format'><small>Format</small></label>
                                                        {
                                                            tih.getFormatOptions(dataType, decimalPlaces).map((o, oi) => {
                                                                return <div
                                                                    key={`update-pretest-input-format-${oi}`}
                                                                    className='custom-control custom-radio mb-2'
                                                                >
                                                                    <input
                                                                        id={`update-pretest-input-format-${oi}`}
                                                                        type='radio'
                                                                        name='updatePretestInputFormat'
                                                                        value={o.type}
                                                                        className='custom-control-input'
                                                                        checked={format === o.type}
                                                                        onClick={handleFormatClick}
                                                                        onChange={handleFormatChange}
                                                                    />
                                                                    <label
                                                                        htmlFor={`update-pretest-input-format-${oi}`}
                                                                        className='custom-control-label'
                                                                    >
                                                                        {o.example}
                                                                    </label>
                                                                </div>
                                                            })
                                                        }
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT_SEARCH) && tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT_REPLACE) ?
                                            <div className='row'>
                                                <div className='col-6'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-pretest-input-format-search'><small>Search <span className='tt-normal fw-400'>(Regular Expression)</span></small></label>
                                                        <select
                                                            id='update-pretest-input-format-search'
                                                            className='form-control'
                                                            value={formatSearch ? formatSearch : ''}
                                                            onChange={handleFormatSearchChange}
                                                        >
                                                            <option></option>
                                                            <option value={JSON.stringify([
                                                                "^([+-]?)(\\d*)(\\d{2})\\.00$",
                                                                "^([+-]?)\\."
                                                            ])}>Rx without Decimal</option>
                                                            <option value={'^0+$'}>Axis ZERO</option>
                                                            <option value={'^[+-]?0(\\.0+)?$'}>Number ZERO</option>
                                                            <option value={'^(\\d+)([+-]\\d*)?$'}>Visual Acuity number</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className='col-6'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-pretest-input-format-replace'><small>Replace <span className='tt-normal fw-400'>(Regular Expression)</span></small></label>
                                                        <select
                                                            id='update-pretest-input-format-replace'
                                                            className='form-control'
                                                            value={formatReplace ? formatReplace : ''}
                                                            onChange={handleFormatReplaceChange}
                                                        >
                                                            <option></option>
                                                            <option value={'["$1$2.$3","$10."]'}>Auto Decimal</option>
                                                            <option value={'Plano'}>Plano</option>
                                                            <option value={'PL'}>PL</option>
                                                            <option value={'SPH'}>SPH</option>
                                                            <option value={'180'}>Axis (0 = 180)</option>
                                                            <option value={'20/$1$2'}>Visual Acuity</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_VALIDATION) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-pretest-input-validation-regex'><small>Validation <span className='tt-normal fw-400'>(Regular Expression)</span></small></label>
                                                        <input
                                                            id='update-pretest-input-validation-regex'
                                                            type='text'
                                                            maxLength={255}
                                                            className='form-control'
                                                            autoComplete='off'
                                                            value={validation ? validation : ''}
                                                            onChange={handleValidationChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_STEP) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_STEP) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-pretest-input-step'><small>Step</small></label>
                                                        <input
                                                            id='update-pretest-input-step'
                                                            type='text'
                                                            maxLength={255}
                                                            className='form-control'
                                                            autoComplete='off'
                                                            value={step ? step : ''}
                                                            onChange={handleStepChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_METADATA_DEFAULT_VALUE) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-pretest-input-default'><small>Default Value</small></label>
                                                        {renderDefaultValueEditor()}
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(pretestInput.data.type, tih.TEMPLATE_INPUT_DESCRIPTION) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-pretest-input-description'><small>Description</small></label>
                                                        <RichTextEditor
                                                            mode='none'
                                                            disableTab={true}
                                                            value={pretestInput.data.descriptionHtml ? pretestInput.data.descriptionHtml : ''}
                                                            onChange={handleDescriptionChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    <div className='row'>
                                        <div className='col-12'>
                                            <div className='form-group mb-4'>
                                                <label className='required' htmlFor='update-pretest-input-key'><small>Key</small></label>
                                                <input
                                                    id='update-pretest-input-key'
                                                    type='text'
                                                    maxLength={100}
                                                    className='form-control'
                                                    autoComplete='off'
                                                    disabled={true}
                                                    value={pretestInput.data.key ? pretestInput.data.key : ''}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </FadeIn>
                    </div>
                    <div className='quick-drawer-action'>
                        <div className='row'>
                            <div className='col-4'>
                            </div>
                            <div className='col-8'>
                                <div className='float-right'>
                                    <button
                                        type='button'
                                        className='btn btn-link btn-cancel mr-2'
                                        onClick={handleCancel}
                                    >Cancel</button>
                                    <button
                                        type='submit'
                                        className='btn btn-success'
                                    >Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
    </>)
}

export default UpdatePretestInput;